// src/modules/ida/components/UsageInfoBadge.tsx
//
// Info badge showing LLM usage on hover: model, provider, route/operation and
// token counts for an assistant message. Prefers the backend-authored usage
// already carried on the message (gateway provenance + top-level usage); falls
// back to lazily fetching the usage endpoint when the message carries none.

import React, { useState, useCallback } from "react";
import {
  Tooltip,
  IconButton,
  Typography,
  Box,
  Stack,
  CircularProgress,
} from "@mui/joy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import type { NormalizedMessageUsage } from "../utils/messageUsage";

/**
 * Usage data returned from the backend `/usage` endpoint (fallback source).
 */
export interface MessageUsage {
  message_id: number;
  model?: string;
  prompt_tokens?: number;
  completion_tokens?: number;
  total_tokens?: number;
}

interface UsageInfoBadgeProps {
  /**
   * Message-derived usage (gateway provenance + top-level). When present it is
   * displayed directly and no fetch occurs. Null/undefined falls back to the
   * lazy fetch below.
   */
  usage?: NormalizedMessageUsage | null;
  /** Conversation ID for the fallback fetch */
  conversationId?: number;
  /** Message ID for the fallback fetch */
  messageId?: number;
  /** Fallback fetch used only when `usage` is absent */
  fetchUsage?: (
    conversationId: number,
    messageId: number,
  ) => Promise<MessageUsage | null>;
  /** Position style - affects alignment */
  position?: "inline" | "absolute";
}

/**
 * Format token count with K/M suffix for large numbers
 */
function formatTokens(tokens: number): string {
  if (tokens >= 1000000) {
    return `${(tokens / 1000000).toFixed(1)}M`;
  }
  if (tokens >= 1000) {
    return `${(tokens / 1000).toFixed(1)}K`;
  }
  return tokens.toString();
}

/** Map the fallback fetch shape into the normalized display shape. */
function normalizeFetchedUsage(
  usage: MessageUsage | null,
): NormalizedMessageUsage | null {
  if (!usage) return null;
  return {
    modelLabel: usage.model,
    inputTokens: usage.prompt_tokens,
    outputTokens: usage.completion_tokens,
    totalTokens:
      usage.total_tokens ??
      (usage.prompt_tokens != null && usage.completion_tokens != null
        ? usage.prompt_tokens + usage.completion_tokens
        : undefined),
  };
}

/** True when the normalized usage carries at least one displayable field. */
function hasDisplayableUsage(usage: NormalizedMessageUsage | null): boolean {
  if (!usage) return false;
  return (
    usage.modelLabel != null ||
    usage.provider != null ||
    usage.operation != null ||
    usage.inputTokens != null ||
    usage.outputTokens != null ||
    usage.totalTokens != null
  );
}

/**
 * Render tooltip content for message usage
 */
function UsageTooltipContent({
  usage,
  loading,
  error,
}: {
  usage: NormalizedMessageUsage | null;
  loading: boolean;
  error: string | null;
}) {
  if (loading) {
    return (
      <Box sx={{ p: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <CircularProgress size="sm" />
        <Typography level="body-xs">Loading...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 0.5 }}>
        <Typography level="body-xs" sx={{ color: "danger.500" }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!hasDisplayableUsage(usage)) {
    return (
      <Box sx={{ p: 0.5 }}>
        <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
          No usage data
        </Typography>
      </Box>
    );
  }

  const resolved = usage as NormalizedMessageUsage;
  const hasTokens =
    resolved.totalTokens != null ||
    resolved.inputTokens != null ||
    resolved.outputTokens != null;

  return (
    <Box sx={{ p: 0.5 }}>
      <Typography level="body-xs" fontWeight="bold" sx={{ mb: 0.5 }}>
        LLM Usage
      </Typography>

      {resolved.modelLabel && (
        <Typography level="body-xs" data-testid="usage-model">
          Model: {resolved.modelLabel}
        </Typography>
      )}
      {resolved.provider && (
        <Typography level="body-xs" data-testid="usage-provider">
          Provider: {resolved.provider}
        </Typography>
      )}
      {resolved.operation && (
        <Typography level="body-xs" data-testid="usage-route">
          Route: {resolved.operation}
        </Typography>
      )}

      {hasTokens && (
        <Stack spacing={0.25} sx={{ mt: 0.5 }}>
          {resolved.totalTokens != null && (
            <Typography level="body-xs" data-testid="usage-total">
              Total: {formatTokens(resolved.totalTokens)} tokens
            </Typography>
          )}
          {resolved.inputTokens != null && (
            <Typography
              level="body-xs"
              sx={{ color: "text.tertiary" }}
              data-testid="usage-input"
            >
              Input: {formatTokens(resolved.inputTokens)}
            </Typography>
          )}
          {resolved.outputTokens != null && (
            <Typography
              level="body-xs"
              sx={{ color: "text.tertiary" }}
              data-testid="usage-output"
            >
              Output: {formatTokens(resolved.outputTokens)}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

/**
 * Info badge component showing LLM usage on hover. Displays message-derived
 * usage directly when present; otherwise lazily fetches usage when the tooltip
 * opens.
 */
export const UsageInfoBadge: React.FC<UsageInfoBadgeProps> = ({
  usage: derivedUsage,
  conversationId,
  messageId,
  fetchUsage,
  position = "inline",
}) => {
  const [fetchedUsage, setFetchedUsage] =
    useState<NormalizedMessageUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);

  const canFetch =
    !derivedUsage &&
    typeof fetchUsage === "function" &&
    typeof conversationId === "number" &&
    typeof messageId === "number";

  const handleTooltipOpen = useCallback(async () => {
    // Message-derived usage needs no fetch; only fetch once as a fallback.
    if (!canFetch || hasFetched) return;

    setLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      const data = await fetchUsage!(conversationId!, messageId!);
      setFetchedUsage(normalizeFetchedUsage(data));
    } catch (err) {
      setError("Failed to load usage");
      console.error("Failed to fetch usage:", err);
    } finally {
      setLoading(false);
    }
  }, [canFetch, conversationId, messageId, fetchUsage, hasFetched]);

  const usage = derivedUsage ?? fetchedUsage;

  return (
    <Tooltip
      title={
        <UsageTooltipContent usage={usage} loading={loading} error={error} />
      }
      placement="top"
      arrow
      variant="outlined"
      onOpen={handleTooltipOpen}
    >
      <IconButton
        size="sm"
        variant="plain"
        color="neutral"
        sx={{
          minWidth: 24,
          minHeight: 24,
          padding: 0,
          opacity: 0.6,
          "&:hover": {
            opacity: 1,
            backgroundColor: "transparent",
          },
          ...(position === "absolute" && {
            position: "absolute",
            top: 4,
            right: 4,
          }),
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Tooltip>
  );
};

export default UsageInfoBadge;
